import React, { useCallback, useEffect, useState } from 'react';

import PageTitle from '../layouts/PageTitle';
import SectionalCommittees from './SectionalCommittees';
import GridNominationCandidate from './NominationShortlist/GridNominationCandidate';
import ScoringView from './Scoring/ScoringView';
import UnconsciousBiasModal from './Scoring/UnconsciousBiasModal';
import FilterControls from './NominationShortlist/FilterControls';
import ScoringButton from './NominationShortlist/ScoringButton';
import ShortlistModals from './NominationShortlist/ShortlistModals';
import CouncilPDFDownloadButton from './NominationShortlist/CouncilPDFDownloadButton';
import CouncilMemberCOIDeclarations from './NominationShortlist/CouncilMemberCOIDeclarations';

import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../context/LoggedInUserContext';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useAdminActionCollections } from '../../context/AdminActionCollections';
import { useScoring } from './hooks/useScoring.js';
import { usePendingCOI } from '../hooks/usePendingCOI';
import { useNotifications } from '../hooks/useNotifications';
import { useShortlistHandlers } from '../hooks/useShortlistHandlers';
import { useCouncilMemberNominations } from '../hooks/useCouncilMemberNominations';

import { addCmseCommitteeToSectionalCommitteeArray } from '../../utils/addCmseCommitteeToSectionalCommitteeArray';
import { getCommitteeCandidateCount } from '../../utils/getCommitteeCandidateCount';
import { getUserSectionalCommittee } from '../../utils/getUserSectionalCommittee.js';
import {
  CM_PRIMARY_ID,
  ELECTION_TYPE,
  NOMINATION_STATUS,
} from '../../utils/constants';

const NominationCandidate = () => {
  const {
    enableCommitteeAccess,
    enableCOI,
    enableScoring,
    enableCouncilMembers,
    enableRecommendedToCouncil,
  } = useAdminActionCollections();
  const { sectionalCommitteesArray } = useStaticCollections();
  const { userDetails } = useLoggedInUserContextProvider();
  const {
    refreshNominations,
    setRefreshNominations,
    committeeArray,
    coiDeclarationArray,
    nominationArray,
    candidateArray,
    coiUpdateInProgress,
    nominationStateArray,
    proposerSeconder,
  } = useAdminCollections();

  const {
    isAdmin,
    isSCChair,
    isSCObserver,
    isCouncilMember,
    userCommittee,
    isSCSecretary,
  } = useUserPermissions(userDetails, committeeArray);

  const {
    allocatedCount,
    recommendedCount,
    nominationsWithCOICount,
    nominationsWithoutCOICount,
  } = useCouncilMemberNominations(userDetails, isCouncilMember, true);

  const isSCMember = userDetails?.roles?.includes('committee-member');

  const sectionalCommitteesWithCmse = addCmseCommitteeToSectionalCommitteeArray(
    sectionalCommitteesArray,
    true,
  );

  const [currentView, setCurrentView] = useState('shortlist');
  const [showTrainingModal, setShowTrainingModal] = useState(false);

  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [selectedNominationStatus, setSelectedStatus] = useState(
    isCouncilMember && !isAdmin ? [8] : null,
  );
  const [selectedRankingFilter, setSelectedRankingFilter] = useState(null);
  const [selectedCOICandidate, setSelectedCOICandidate] = useState(null);
  const [hasScoreableCandidates, setHasScoreableCandidates] = useState(false);
  const [lastRankedCount, setLastRankedCount] = useState(null);
  const [showMyAllocations, setShowMyAllocations] = useState(false);
  const {
    notificationMessage,
    notificationTitle,
    notificationIcon,
    notificationIconColor,
    showNotification,
  } = useNotifications(60000);

  const {
    showModal,
    setShowModal,
    selectedCandidate,
    setSelectedCandidate,
    selectedNominationId,
    showStage2Modal,
    setShowStage2Modal,
    selectedStage2Candidate,
    setSelectedStage2Candidate,
    selectedStage2NominationId,
    handleSelectClick,
    handleStage2SelectClick,
    handleCancelSelect,
    handleCancelStage2Select,
    handleRankingSuccess,
  } = useShortlistHandlers(showNotification);

  const useScoringResults = useScoring(
    sectionalCommitteesWithCmse,
    userDetails,
    (title) =>
      showNotification(
        title,
        notificationMessage,
        notificationIcon,
        notificationIconColor,
      ),
    (message) =>
      showNotification(
        notificationTitle,
        message,
        notificationIcon,
        notificationIconColor,
      ),
    committeeArray,
    nominationArray,
    candidateArray,
  );

  const {
    trainingCompleted,
    isLoading,
    handleTrainingComplete,
    getCommitteeInfo,
  } = useScoringResults;

  const hasPendingCOI = usePendingCOI(
    userDetails,
    isSCMember,
    isSCChair,
    coiDeclarationArray,
    nominationArray,
    getCommitteeInfo,
  );

  const handleGetCommitteeCandidateCount = useCallback(
    (committeeId) => {
      if (!nominationArray || !candidateArray || !proposerSeconder) {
        return 0;
      }

      return getCommitteeCandidateCount({
        committeeId,
        nominationArray,
        candidateArray,
        nominationStateArray,
        proposerSeconder,
        showMyAllocations,
        isCouncilMember,
        isAdmin,
        userId: userDetails?.id,
        selectedNominationStatus,
        selectedRankingFilter,
      });
    },
    [
      nominationArray,
      candidateArray,
      nominationStateArray,
      proposerSeconder,
      showMyAllocations,
      isCouncilMember,
      isAdmin,
      userDetails?.id,
      selectedNominationStatus,
      selectedRankingFilter,
    ],
  );

  const { rankedCount, recommendedToCouncilCount, overlapNominationsCount } =
    React.useMemo(() => {
      if (!nominationArray)
        return {
          rankedCount: 0,
          recommendedToCouncilCount: 0,
          overlapNominationsCount: 0,
        };

      const getCommitteeId = (nom) => {
        if (nom.attributes.electionTypeId === ELECTION_TYPE.SPECIAL_ELECTION)
          return 15;
        if (
          nom.attributes.electionTypeId === ELECTION_TYPE.CORRESPONDING_MEMBER
        )
          return 14;
        return nom.attributes.sectionalCommitteeId;
      };

      const isAuthorizedUser = isAdmin || isSCSecretary || isCouncilMember;

      const inferredCommitteeId =
        selectedCommittee ||
        getUserSectionalCommittee(userDetails, committeeArray);

      let stage2Noms;

      if (isAuthorizedUser) {
        stage2Noms = nominationArray.filter(
          (nom) =>
            nom.attributes.nominationStatusTypeId ===
            NOMINATION_STATUS.RECOMMENDED,
        );
      } else {
        stage2Noms = nominationArray.filter((nom) => {
          const isStage2 =
            nom.attributes.nominationStatusTypeId ===
            NOMINATION_STATUS.RECOMMENDED;
          const committeeMatches =
            !inferredCommitteeId || getCommitteeId(nom) === inferredCommitteeId;

          return isStage2 && committeeMatches;
        });
      }

      const rCount =
        nominationStateArray?.filter(
          (state) =>
            state.attributes.ranking &&
            stage2Noms.some((nom) => nom.id === state.attributes.nominationId),
        ).length || 0;

      const overlapNoms = nominationArray.filter(
        (nom) =>
          nom.attributes.overlapSectionalCommitteeId &&
          (isAuthorizedUser ||
            nom.attributes.sectionalCommitteeId === inferredCommitteeId ||
            nom.attributes.overlapSectionalCommitteeId ===
              inferredCommitteeId) &&
          nom.attributes.sectionalCommitteeId !==
            nom.attributes.overlapSectionalCommitteeId &&
          nom.attributes.electionTypeId === ELECTION_TYPE.ORDINARY &&
          nom.attributes.overlapSectionalCommitteeId !== 14 &&
          [
            NOMINATION_STATUS.SUBMITTED,
            NOMINATION_STATUS.SHORTLISTED,
            NOMINATION_STATUS.RECOMMENDED,
          ].includes(nom.attributes.nominationStatusTypeId),
      ).length;

      return {
        rankedCount: rCount,
        recommendedToCouncilCount: stage2Noms.length,
        overlapNominationsCount: overlapNoms,
      };
    }, [selectedCommittee, nominationArray, nominationStateArray]);

  const isAllRanked =
    recommendedToCouncilCount > 0 &&
    recommendedToCouncilCount - rankedCount === 0;

  useEffect(() => {
    if (
      selectedRankingFilter === 'unranked' &&
      isAllRanked &&
      recommendedToCouncilCount > 0
    ) {
      setSelectedRankingFilter('ranked');

      showNotification(
        'All Ranked!',
        'You have finished ranking all recommended candidates for this committee.',
        'fa-check-circle',
        'text-success',
      );
    }
  }, [
    isAllRanked,
    selectedRankingFilter,
    recommendedToCouncilCount,
    setSelectedRankingFilter,
  ]);

  useEffect(() => {
    if (isSCChair && selectedCommittee && recommendedToCouncilCount) {
      if (recommendedToCouncilCount > 0 && rankedCount < 2) {
        const initialDelay = lastRankedCount === null ? 500 : 12000;
        const initialTimer = setTimeout(() => {
          showNotification(
            'Ranking Reminder',
            'Remember to rank at least two candidates for Council consideration. Rankings help ensure a comprehensive selection process.',
            'fa-info-circle',
            'text-info',
          );
          setLastRankedCount(rankedCount);
        }, initialDelay);

        return () => clearTimeout(initialTimer);
      }

      if (rankedCount >= 2) {
        setLastRankedCount(rankedCount);
      }
    }
  }, [
    isSCChair,
    isAdmin,
    selectedCommittee,
    recommendedToCouncilCount,
    rankedCount,
    lastRankedCount,
    showNotification,
  ]);

  useEffect(() => {
    if (isSCChair && selectedCommittee && recommendedToCouncilCount) {
      if (recommendedToCouncilCount >= 4) {
        const timer = setTimeout(() => {
          showNotification(
            'Council Recommendation Limit',
            'You have reached the maximum of 4 Council recommendations for this committee.',
            'fa-info-circle',
            'text-info',
          );
        }, 3000);

        return () => clearTimeout(timer);
      }
    }
  }, [
    isSCChair,
    isAdmin,
    selectedCommittee,
    recommendedToCouncilCount,
    showNotification,
  ]);

  useEffect(() => {
    if (!enableCommitteeAccess) {
      showNotification(
        'Nominations are now closed',
        ' Nominations are now closed, please contact fellowship@science.org.au.',
        null,
        null,
      );
    }
  }, [enableCommitteeAccess, showNotification]);

  useEffect(() => {
    if (
      !isAdmin &&
      !isSCObserver &&
      !isCouncilMember &&
      userCommittee &&
      !selectedCommittee
    ) {
      const isPairCommittee = userCommittee.id === CM_PRIMARY_ID;

      if (!isPairCommittee) {
        if (!isCouncilMember || !enableCouncilMembers) {
          setSelectedCommittee(userCommittee.id);
        }
      }
    }
  }, [
    isCouncilMember,
    isAdmin,
    isSCObserver,
    userCommittee,
    selectedCommittee,
    enableCouncilMembers,
  ]);

  useEffect(() => {
    if (!showModal && selectedCandidate) {
      showNotification(
        `Shortlisted`,
        `${selectedCandidate} has been successfully shortlisted.`,
        'fa-check',
        'text-shortlisted',
      );
      setSelectedCandidate(null);
    }
  }, [showModal, selectedCandidate, showNotification, setSelectedCandidate]);

  useEffect(() => {
    if (!showStage2Modal && selectedStage2Candidate) {
      showNotification(
        `Recommended to Council`,
        `${selectedStage2Candidate} has been successfully shortlisted for Council consideration.`,
        'fa-star',
        'text-council-shortlisted',
      );
      setSelectedStage2Candidate(null);
    }
  }, [
    showStage2Modal,
    selectedStage2Candidate,
    showNotification,
    setSelectedStage2Candidate,
  ]);

  const handleCommitteeChange = (committeeId) => {
    setSelectedCommittee(committeeId);
    setLastRankedCount(null);
  };

  const handleStartScoring = () => {
    if (!trainingCompleted) {
      setShowTrainingModal(true);
      return;
    }
    setCurrentView('scoring');
  };

  const handleTrainingCompleteAndNavigate = async () => {
    await handleTrainingComplete();
    setShowTrainingModal(false);
    setCurrentView('scoring');
  };

  const handleBackToShortlist = () => {
    setCurrentView('shortlist');
  };

  const showCOIFeatures =
    enableCOI &&
    (isSCChair ||
      isSCMember ||
      isSCObserver ||
      (enableCouncilMembers && isCouncilMember) ||
      isAdmin);
  const canScore = isSCMember || isSCChair;
  const showScoringButton = enableScoring && canScore;

  const showCouncilPDFButton =
    enableCouncilMembers && isCouncilMember && !isAdmin;

  if (currentView === 'scoring' && trainingCompleted && !hasPendingCOI) {
    return (
      <ScoringView
        onBackToShortlist={handleBackToShortlist}
        userDetails={userDetails}
        committeeArray={committeeArray}
        sectionalCommitteesWithCmse={sectionalCommitteesWithCmse}
        setNotificationTitle={(title) =>
          showNotification(
            title,
            notificationMessage,
            notificationIcon,
            notificationIconColor,
          )
        }
        setNotificationMessage={(message) =>
          showNotification(
            notificationTitle,
            message,
            notificationIcon,
            notificationIconColor,
          )
        }
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
        nominationArray={nominationArray}
        candidateArray={candidateArray}
        useScoringResults={useScoringResults}
      />
    );
  }
  return (
    <>
      <PageTitle
        motherMenu='Nominations'
        activeMenu='Candidates'
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
        notificationIcon={notificationIcon}
        notificationIconColor={notificationIconColor}
      />

      {showScoringButton && hasScoreableCandidates && (
        <ScoringButton
          isLoading={isLoading}
          coiUpdateInProgress={coiUpdateInProgress}
          hasPendingCOI={hasPendingCOI}
          trainingCompleted={trainingCompleted}
          handleStartScoring={handleStartScoring}
        />
      )}

      {showCouncilPDFButton && (
        <CouncilPDFDownloadButton onNotification={showNotification} />
      )}

      <FilterControls
        isAdmin={isAdmin}
        isSCChair={isSCChair}
        isSCObserver={isSCObserver}
        isSCSecretary={isSCSecretary}
        isSCMember={isSCMember}
        isCouncilMember={isCouncilMember}
        showCOIFeatures={showCOIFeatures}
        selectedCommittee={selectedCommittee}
        sectionalCommitteesWithCmse={sectionalCommitteesWithCmse}
        handleCommitteeChange={handleCommitteeChange}
        selectedNominationStatus={selectedNominationStatus}
        setSelectedStatus={setSelectedStatus}
        selectedRankingFilter={selectedRankingFilter}
        setSelectedRankingFilter={setSelectedRankingFilter}
        selectedCOICandidate={selectedCOICandidate}
        setSelectedCOICandidate={setSelectedCOICandidate}
        userDetails={userDetails}
        setNotificationTitle={(title) =>
          showNotification(
            title,
            notificationMessage,
            notificationIcon,
            notificationIconColor,
          )
        }
        setNotificationMessage={(message) =>
          showNotification(
            notificationTitle,
            message,
            notificationIcon,
            notificationIconColor,
          )
        }
        showMyAllocations={showMyAllocations}
        setShowMyAllocations={setShowMyAllocations}
        getCommitteeCandidateCount={handleGetCommitteeCandidateCount}
        enableRecommendedToCouncil={enableRecommendedToCouncil}
        recommendedToCouncilCount={recommendedToCouncilCount}
        rankedCount={rankedCount}
        overlapNominationsCount={overlapNominationsCount}
      />

      {isCouncilMember && !isAdmin && (
        <CouncilMemberCOIDeclarations
          allocatedCount={allocatedCount}
          recommendedCount={recommendedCount}
          nominationsWithCOICount={nominationsWithCOICount}
          nominationsWithoutCOICount={nominationsWithoutCOICount}
        />
      )}

      <SectionalCommittees
        TableComponent={GridNominationCandidate}
        statusTypes={[
          NOMINATION_STATUS.SUBMITTED,
          NOMINATION_STATUS.SHORTLISTED,
          NOMINATION_STATUS.RECOMMENDED,
        ]}
        handleSelectClick={handleSelectClick}
        handleStage2SelectClick={handleStage2SelectClick}
        handleCOIClick={
          showCOIFeatures
            ? (candidate) => setSelectedCOICandidate(candidate)
            : null
        }
        selectedCommittee={selectedCommittee}
        selectedNominationStatus={selectedNominationStatus}
        selectedRankingFilter={selectedRankingFilter}
        showMyAllocations={showMyAllocations}
        onCandidateCountChange={setHasScoreableCandidates}
        onRankingSuccess={handleRankingSuccess}
        enableCouncilMembers={enableCouncilMembers}
        isCouncilBannerVisible={isCouncilMember && !isAdmin}
      />

      <ShortlistModals
        showModal={showModal}
        handleCancelSelect={handleCancelSelect}
        selectedCandidate={selectedCandidate}
        selectedNominationId={selectedNominationId}
        setShowModal={setShowModal}
        showStage2Modal={showStage2Modal}
        handleCancelStage2Select={handleCancelStage2Select}
        selectedStage2Candidate={selectedStage2Candidate}
        selectedStage2NominationId={selectedStage2NominationId}
        setShowStage2Modal={setShowStage2Modal}
        refreshNominations={refreshNominations}
        setRefreshNominations={setRefreshNominations}
      />

      <UnconsciousBiasModal
        show={showTrainingModal}
        onHide={() => setShowTrainingModal(false)}
        onTrainingComplete={handleTrainingCompleteAndNavigate}
      />
    </>
  );
};

export default NominationCandidate;
